import { Flex, Text } from "@wayflyer/flyui";
import { ColumnChart, type ColumnChartYAxis, type ReferencedChartProps } from "@wayflyer/flyui-dataviz";
import { useIntl } from "react-intl";

import { getLtvCacColumnValues } from "./chart-data";

export interface LtvCacChartProps extends ReferencedChartProps {
  currentValueName?: string;
  referenceValueName?: string;
  isCustomer?: boolean;
  separateYAxis?: boolean;
  unavailable?: boolean;
}

const referenceColor = "var(--palette-dataviz-categorical-category1)" as const;
const incompleteColor = "var(--palette-dataviz-sequential-caelum-300)" as const;
const currentColor = "var(--palette-dataviz-categorical-category2)" as const;
const customerColor = "var(--palette-content-neutral-subtle)" as const;

export const LtvCac = ({
  data,
  formatValue,
  width = 0,
  isCustomer = false,
  loading = false,
  unavailable = false,
  currentValueName = "",
  referenceValueName = "",
  separateYAxis = false,
}: Optional<LtvCacChartProps, "formatValue">) => {
  const intl = useIntl();
  const currentName =
    currentValueName || intl.formatMessage({ id: "CAC", defaultMessage: "Customer Acquisition Cost" });
  const referenceName =
    referenceValueName ||
    (isCustomer
      ? intl.formatMessage({ id: "Customers", defaultMessage: "Customers" })
      : intl.formatMessage({ id: "Internal LTV / CAC: LTV Chart Name", defaultMessage: "LTV" }));
  const incompleteName = `${referenceName} (${intl.formatMessage({
    id: "LTV - incomplete",
    defaultMessage: "incomplete",
  })})`;
  const rows = data ?? [];
  const categories = rows.map((_, index) => String(index));
  const formatCategory = (category: string) => rows[Number(category)]?.day ?? "";
  const formatCurrent = (value: number) => String(formatValue ? formatValue(value, false) : value);
  const formatReference = (value: number) => String(formatValue ? formatValue(value, true) : value);
  const axes: ColumnChartYAxis[] = [
    {
      id: "reference",
      position: "left",
      label: separateYAxis ? referenceName : undefined,
      formatValue: formatReference,
      formatTick: formatReference,
      width: 72,
    },
  ];
  if (separateYAxis) {
    axes.push({
      id: "current",
      position: "right",
      label: "CAC",
      formatValue: formatCurrent,
      formatTick: formatCurrent,
      width: 72,
    });
  }
  const columnColor = isCustomer ? customerColor : referenceColor;

  return (
    <Flex width={width ? `${width}px` : "100%"} direction="column" flex={1}>
      <ColumnChart.Root
        label={`${referenceName} / ${currentName}`}
        categories={categories}
        yAxes={axes}
        status={unavailable ? "unavailable" : loading ? "loading" : rows.length ? "ready" : "empty"}
      >
        <ColumnChart.Plot formatCategory={formatCategory} formatY={formatReference}>
          <ColumnChart.Series
            seriesKey="reference"
            label={referenceName}
            data={getLtvCacColumnValues(rows, "referenceValue")}
            color={columnColor}
            stack="cohort"
            yAxis="reference"
          />
          {!isCustomer ? (
            <ColumnChart.Series
              seriesKey="incomplete"
              label={incompleteName}
              data={getLtvCacColumnValues(rows, "referenceContinuationValue")}
              color={incompleteColor}
              stack="cohort"
              yAxis="reference"
            />
          ) : null}
          <ColumnChart.Line
            seriesKey="current"
            label={currentName}
            data={getLtvCacColumnValues(rows, "currentValue")}
            color={currentColor}
            curve="monotone"
            yAxis={separateYAxis ? "current" : "reference"}
          />
          <ColumnChart.Inspector>
            {(point) => (
              <ColumnChart.Tooltip title={formatCategory(point.category)}>
                {point.entries.map((entry) => (
                  <ColumnChart.TooltipRow
                    key={entry.seriesKey}
                    label={entry.label}
                    value={entry.seriesKey === "current" ? formatCurrent(entry.value) : formatReference(entry.value)}
                  />
                ))}
              </ColumnChart.Tooltip>
            )}
          </ColumnChart.Inspector>
        </ColumnChart.Plot>
        <ColumnChart.Legend>
          <ColumnChart.LegendItem color={columnColor}>{referenceName}</ColumnChart.LegendItem>
          {!isCustomer ? (
            <ColumnChart.LegendItem color={incompleteColor}>{incompleteName}</ColumnChart.LegendItem>
          ) : null}
          <ColumnChart.LegendItem color={currentColor} fill="line">
            {currentName}
          </ColumnChart.LegendItem>
        </ColumnChart.Legend>
      </ColumnChart.Root>
      {!loading && !unavailable && !rows.length ? (
        <Text color="subtle" size="sm">
          {intl.formatMessage({ id: "Internal LTV / CAC: Empty Chart", defaultMessage: "No data yet" })}
        </Text>
      ) : null}
    </Flex>
  );
};
